import React, { Suspense, useEffect } from 'react'
import styled from 'styled-components'
import qs from 'query-string'
import { Column } from 'components/common/layout'
import { useRouter } from 'next/router'
import { SemiBoldText } from 'components/common/text'
import { theme } from 'infra/theme'
import { useStores } from 'stores/globals'
import { observer } from 'mobx-react'
import {
  ExitTextButton,
  FloatingButton,
  FormBackground,
  RequestFormButtonContainer,
  RequestFormTop,
  TopNavigator,
} from 'components/scouts/common/styles'
import { analytics } from 'infra/analytics'
import { useRequestFormOptions, useScoutMyRequest, useUser } from 'apis/hooks'
import { FiArrowLeft } from 'react-icons/fi'
import { BtnType } from 'components/common/button'
import { ConditionLaterModal } from 'components/scouts/request-form/condition/condition-later-modal'
import {
  getJobConditionToPositionFilterParams,
  getProfileCompleteStatus,
} from 'utils/formatters'
import { POSITION_FILTER_QUERY_PARAMS_KEY } from 'infra/constants'
import toast from 'react-hot-toast'
import { RoutePaths, RouterQueryKey } from 'types/router'
import { useIsDesktopCSR } from 'utils/hooks'
import { mutate } from 'swr'
import { UrlObject } from 'url'

export type ConditionFormPage =
  | 'job-search-status'
  | 'location'
  | 'industries'
  | 'size'
  | 'investment'
  | 'salary'
  | 'preference'

export const REQUEST_FORM_PAGES: ConditionFormPage[] = [
  'job-search-status',
  'location',
  'industries',
  'size',
  'investment',
  'salary',
  'preference', // progress bar 카운트에 포함하지 않음
]

const REQUEST_FORM_PAGE_COUNT = REQUEST_FORM_PAGES.length
const LAST_PAGE_INDEX = REQUEST_FORM_PAGE_COUNT - 1

const routerQueryForMyProfile = {
  [RouterQueryKey.IS_FROM_REQUEST_FORM]: 'true',
} // 작성폼에서 내프로필로 가는 경우, 이 파라미터 필요

export const ConditionFormContainer: React.FCC = observer(({ children }) => {
  const { data: user } = useUser()
  const { data: myRequest } = useScoutMyRequest()
  const { data: options } = useRequestFormOptions()
  if (!options) return null
  const { applyModalStore } = useStores()
  const router = useRouter()
  const shouldApplyConditions = !!router.query.shouldApplyConditions
  const isUpdatingCondition = !!router.query.isUpdatingCondition
  const fromSite = router.query[RouterQueryKey.FROM_SITE]
  const startupId = Number(router.query[RouterQueryKey.STARTUP_ID]) || undefined
  const positionId =
    Number(router.query[RouterQueryKey.POSITION_ID]) || undefined
  const isDesktop = useIsDesktopCSR()
  const { isCompletedCondition } = getProfileCompleteStatus(myRequest)
  const { scoutConditionFormStore } = useStores()
  const PAGE_NAME = router.pathname.split(
    '/scouts/request-form/',
  )[1] as ConditionFormPage
  const PAGE_INDEX = REQUEST_FORM_PAGES.indexOf(PAGE_NAME)
  const isEditing = isUpdatingCondition && isCompletedCondition

  // 작성폼 플로우로 들어오기 전 페이지로 이동
  const navigateToPrevPage = () => {
    // 1. 기본값: 내 프로필
    let params: UrlObject | string = {
      pathname: RoutePaths.MY_PROFILE,
      query: routerQueryForMyProfile,
    }
    // 2. fromSite 있을 때
    if (typeof fromSite === 'string' && fromSite.length > 0) {
      params = {
        pathname: fromSite,
        query: fromSite.includes(RoutePaths.MY_PROFILE)
          ? routerQueryForMyProfile
          : undefined,
      }
    }
    // 3. 지원하기에서 넘어왔을 때
    if (startupId && positionId) {
      if (isDesktop) {
        params = { pathname: RoutePaths.POSITIONS }
        applyModalStore.openModal(positionId, startupId)
      } else {
        params = {
          pathname: RoutePaths.POSITION_APPLICATION,
          query: {
            [RouterQueryKey.STARTUP_ID]: String(startupId),
            [RouterQueryKey.POSITION_ID]: String(positionId),
          },
        }
      }
    }
    router.push(params)
  }

  const moveToBack = async () => {
    if (scoutConditionFormStore.isLoading) return
    if (PAGE_INDEX === 0) return navigateToPrevPage()
    try {
      if (scoutConditionFormStore.isValid(PAGE_NAME) === false)
        return alert('필수 입력 항목을 완료해주세요.')
      await scoutConditionFormStore.save(
        isCompletedCondition ? 0 : PAGE_INDEX,
        PAGE_NAME,
      )
      router.push({
        pathname: REQUEST_FORM_PAGES[PAGE_INDEX - 1],
        query: router.query,
      })
    } catch (e) {
      alert(e)
      return
    }
  }

  const handleJobConditionToPositionFilterParams = () => {
    if (!user || !myRequest) return
    const params = getJobConditionToPositionFilterParams(
      user,
      myRequest,
      options.locations,
    )
    const queryParams = qs.stringify(params)
    localStorage.setItem(POSITION_FILTER_QUERY_PARAMS_KEY, queryParams)
    toast('내 구직 조건에 딱 맞게 필터를 설정했어요')
    router.push('/positions')
  }

  const moveToNext = async () => {
    try {
      if (scoutConditionFormStore.isLoading) return
      await scoutConditionFormStore.save(
        // 이미 프로필 작성 플로우 거친 후 조건 수정하는 상황이나 조건 등록까지 끝낸 후에는 다시 들어와도 페이지 이동시켜줄 필요 없음 => 0
        isCompletedCondition || PAGE_INDEX == LAST_PAGE_INDEX
          ? 0
          : PAGE_INDEX + 1,
        PAGE_NAME,
      )
      if (shouldApplyConditions && PAGE_INDEX === LAST_PAGE_INDEX) {
        handleJobConditionToPositionFilterParams()
        return
      }
      if (PAGE_INDEX === LAST_PAGE_INDEX) await mutate('/scouts/requests/me')
      router.push({
        pathname:
          PAGE_INDEX === LAST_PAGE_INDEX
            ? isUpdatingCondition
              ? RoutePaths.MY_PROFILE
              : RoutePaths.REQUEST_FORM_EDUCATION
            : `${REQUEST_FORM_PAGES[PAGE_INDEX + 1]}`,
        query: router.query,
      })
    } catch (e) {
      alert(e)
    }
  }

  useEffect(() => {
    // 스토어에 필수 항목인 workLevel 값이 초기값 상태일 때만 불러와야함
    if (scoutConditionFormStore.form.workLevel !== null) return
    if (myRequest) return scoutConditionFormStore.setMyRequest(myRequest)
  }, [myRequest, scoutConditionFormStore])

  return (
    <>
      <FormBackground>
        <RequestFormTop>
          <ProgressBar pageNumber={PAGE_INDEX + 1}>
            <div style={{ backgroundColor: theme.color.primary }} />
          </ProgressBar>
          <ConditionFormLayout>
            <TopNavigator>
              <FiArrowLeft
                size={22}
                color={theme.color.black3}
                onClick={() => {
                  moveToBack()
                  analytics.track('click_sr_prev_btn')
                }}
                style={{ cursor: 'pointer' }}
              />
              <ExitTextButton
                onClick={async () => {
                  // 최초 조건 등록 중 (나중에 하기)
                  if (!isEditing)
                    return scoutConditionFormStore.setIsLaterModalOpen(true)
                  // 조건 수정 중 (수정 끝내기)
                  if (scoutConditionFormStore.isValid(PAGE_NAME) === false)
                    return alert('필수 입력 항목을 완료해주세요.')
                  await scoutConditionFormStore.save(0, PAGE_NAME)
                  if (shouldApplyConditions)
                    return handleJobConditionToPositionFilterParams()
                  router.push(RoutePaths.MY_PROFILE)
                }}
              >
                {isEditing ? '수정 끝내기' : '나중에 하기'}
              </ExitTextButton>
              <Step>
                {PAGE_INDEX + 1 < REQUEST_FORM_PAGE_COUNT ? (
                  <>
                    <span style={{ color: theme.color.primary }}>
                      {PAGE_INDEX + 1}
                    </span>
                    /{REQUEST_FORM_PAGE_COUNT - 1}
                  </>
                ) : (
                  '🎉'
                )}
              </Step>
            </TopNavigator>
          </ConditionFormLayout>
        </RequestFormTop>
        <Content>{children}</Content>
      </FormBackground>
      <RequestFormButtonContainer>
        <FloatingButton
          variant={
            scoutConditionFormStore.isValid(PAGE_NAME) === false
              ? BtnType.DISABLE
              : scoutConditionFormStore.isFilled(PAGE_NAME) === false
              ? BtnType.PRIMARY2
              : BtnType.PRIMARY
          }
          onClick={() => {
            if (scoutConditionFormStore.isValid(PAGE_NAME) === false) return
            moveToNext()
            analytics.track('click_sr_next_btn')
          }}
        >
          {PAGE_INDEX === LAST_PAGE_INDEX
            ? '구직 조건 선택 완료하기'
            : scoutConditionFormStore.isFilled(PAGE_NAME) === false
            ? '상관 없어요'
            : '다음'}
        </FloatingButton>
      </RequestFormButtonContainer>
      {user && scoutConditionFormStore.isLaterModalOpen && (
        <Suspense>
          <ConditionLaterModal
            user={user}
            onClose={() => scoutConditionFormStore.setIsLaterModalOpen(false)}
          />
        </Suspense>
      )}
    </>
  )
})

const ProgressBar = styled.div<{ pageNumber: number }>`
  height: 8px;
  display: grid;
  grid-template-columns:
    calc(100% / ${REQUEST_FORM_PAGE_COUNT - 1} * ${(p) => p.pageNumber})
    1fr;
  background-color: ${(p) => p.theme.color.gray5};
`

const ConditionFormLayout = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 500px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 0 20px;
    max-width: 540px;
  }
`

const Content = styled(Column)`
  margin: 145px 0;
  width: 1060px;
  height: 100%;
  align-items: center;
  @media ${(p) => p.theme.deviceSize.mobile} {
    margin: 80px 20px 140px;
    width: 100%;
  }
`

const Step = styled(SemiBoldText)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${(p) => p.theme.color.gray2};
  @media ${(p) => p.theme.deviceSize.desktop} {
    font-size: ${(p) => p.theme.fontSize.h5};
  }
`
